import {
  ArrowDownIcon,
  ArrowUpIcon,
  BarsArrowDownIcon,
  BoltIcon,
  BoltSlashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { PageOrderModal } from 'components/Modals/PageOrder'
import { SaveChanges } from 'components/SaveChanges'
import {
  COMPANY_NAME_FC,
  FieldOrigin,
  ILoanFieldPos,
  InputSelect,
  InputType,
  ISelectRangeOption,
  ISelectValueOption,
  IVisibleLogic,
} from 'config'
import {
  CustomFieldType,
  customFieldType2Input,
  CustomFieldTypeOptions,
  getFieldOriginOptions,
  getLoanInputFieldOptions,
  getLoanInputFields,
  isAccountControlField,
  isUtmCampaignField,
} from 'config/loan.input.fields.constants'
import React, { useEffect, useMemo, useState } from 'react'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getAdminConfig, setAdminConfig } from 'services'
import { Button, Input2, InputVisibleLogic, Tooltip } from 'stories/components'
import {
  capitalizeFirstLetter,
  confirm,
  getNewSelectionOptions,
  openUserSearchDlg,
  prompt,
  sortByKey,
  toCamelCase,
} from 'utils'
import { copyClipboard } from 'utils/copyClipboard'
import { RenderInput } from 'utils/RenderInput'

import { LinkToParty } from '../Configuration/LoanFieldsProperties/LinkToParty'
import { etcConfigDefaultInputs, ignoredFields, isMonthlyInput, requiredFields } from './config'
import {
  getFieldOriginOptionsExceptCustom,
  IBorrowerLoanWorkflowControl,
  IBorrowerLoanWorkflowCustomInput,
  IBorrowerLoanWorkflowCustomInputSelect,
  IBorrowerLoanWorkflowForm,
  IBorrowerLoanWorkflowSelect,
  IBorrowerLoanWorkflowStep,
  ISelectOption,
} from './types'

export const BorrowerPipelineConfiguration = (props: { type: 'borrower' | 'broker'; setChanged: Function }) => {
  const { type: pageType } = props
  const isBorrower = pageType == 'borrower'

  const [isLoading, setLoading] = useState(false)
  const [configs, setConfigs] = useState<IBorrowerLoanWorkflowStep[]>([])
  const [etcConfig, setEtcConfig] = useState<Record<string, any>>({})
  const [changed, _setChanged] = useState(false)
  const defaultHeaderText = `Welcome to your ${COMPANY_NAME_FC} loan application, **Let's get started!**`

  const setChanged = (value: boolean) => {
    _setChanged(value)
    props.setChanged(value)
  }

  const inputFields = useMemo(() => {
    const inputs = getLoanInputFields({
      custom: true,
      other: true,
    })

    const customOptions: Record<string, InputType> = {}
    configs.forEach((config) => {
      if (config.type != 'form') return
      config.inputs
        .filter((v) => v.fieldOrigin == FieldOrigin.Custom)
        .forEach((_input) => {
          const input = _input as IBorrowerLoanWorkflowCustomInput
          const inputObj = {
            ...customFieldType2Input(input.fieldType),
            title: input.title,
          }
          if (input.fieldType == CustomFieldType.Select) {
            ;(inputObj as any).options = getNewSelectionOptions(
              [],
              (input as IBorrowerLoanWorkflowCustomInputSelect).options,
            )
          }

          customOptions[input.fieldKey] = inputObj
        })
    })
    inputs.Custom = customOptions

    return inputs
  }, [configs])

  const allInputOptions = useMemo(() => {
    const options = getLoanInputFieldOptions('', { ignoredFields })

    const customOptions: Record<string, string> = {}
    configs.forEach((config) => {
      if (config.type != 'form') return
      config.inputs
        .filter((v) => v.fieldOrigin == FieldOrigin.Custom)
        .forEach((_input) => {
          const input = _input as IBorrowerLoanWorkflowCustomInput
          customOptions[input.fieldKey] = `[${capitalizeFirstLetter(input.fieldType)}] ${input.title}`
        })
    })
    options.Custom = customOptions

    return options
  }, [configs])

  const etcConfigInputs = etcConfigDefaultInputs()

  const fieldOriginOptions = getFieldOriginOptions()
  const fieldOriginOptionsExceptCustom = getFieldOriginOptionsExceptCustom()

  const selectInputOptions = useMemo(() => getLoanInputFieldOptions('select', { ignoredFields }), [])
  const [errorFields, setErrorFields] = useState<Array<{ fieldOrigin: FieldOrigin; fieldKey: string }>>([])
  const [orderPageIndex, setOrderPageIndex] = useState(-1)

  useEffect(() => {
    setLoading(true)
    getAdminConfig('loanWorkflow').then((data) => setEtcConfig(data))
    getAdminConfig(`${pageType}LoanWorkflow`)
      .then((configs) => setConfigs(configs))
      .finally(() => setLoading(false))
  }, [])

  const onChange = (index: number, key: string, value: string) => {
    const newConfigs = cloneDeep(configs)
    ;(newConfigs[index] as any)[key] = value
    if (key === 'type') (newConfigs[index] as any).inputs = []
    setConfigs(newConfigs)
    setChanged(true)
  }

  const onAddStep = () => {
    const newConfigs = cloneDeep(configs)
    newConfigs.push({
      type: '',
      title: '',
      inputs: [],
    } as any)
    setConfigs(newConfigs)
    setChanged(true)
  }

  const spliceRequiredField = (fields: any[], fieldOrigin: FieldOrigin, fieldKey: string) => {
    const index = fields.findIndex((v: any) => v.fieldOrigin === fieldOrigin && v.fieldKey === fieldKey)
    if (index != -1) fields.splice(index, 1)
    return index != -1
  }

  const hasField = (fieldOrigin: FieldOrigin, fieldKey: string) => {
    return !!configs.find((config) => {
      if (config.type === 'select') {
        return config.fieldOrigin === fieldOrigin && config.fieldKey === fieldKey
      } else {
        return !!config.inputs.find(
          (input) => input.fieldOrigin == FieldOrigin.LoanStructure && input.fieldKey == fieldKey,
        )
      }
    })
  }

  const onSave = () => {
    const fields = cloneDeep(requiredFields)
    const erroredFields: ILoanFieldPos[] = []
    fields.forEach(({ needed, required }) => {
      if (needed && !hasField(needed.fieldOrigin, needed.fieldKey)) return

      configs.forEach((config) => {
        if (config.type === 'select') {
          spliceRequiredField(required, config.fieldOrigin, config.fieldKey)
        } else {
          config.inputs.forEach((input) => spliceRequiredField(required, input.fieldOrigin, input.fieldKey))
        }
      })

      erroredFields.push(...required)
    })

    setErrorFields(erroredFields)
    if (erroredFields.length != 0) return

    setLoading(true)
    setAdminConfig(`${pageType}LoanWorkflow`, configs)
      .then(() =>
        toast(`${capitalizeFirstLetter(pageType)} Loan Pipeline Configuration has been saved.`, { type: 'info' }),
      )
      .finally(() => {
        setLoading(false)
        setChanged(false)
      })
  }

  const onChangePageOrder = (pageIndex: number) => {
    setOrderPageIndex(pageIndex)
  }

  const onMovePage = (index: number, dir: 1 | -1) => {
    if (index + dir < 0 || index + dir >= configs.length) return

    const newConfigs = cloneDeep(configs)
    const curItem = newConfigs[index]
    newConfigs[index] = newConfigs[index + dir]
    newConfigs[index + dir] = curItem
    setConfigs(newConfigs)
    setChanged(true)
  }

  const onRemovePage = async (index: number) => {
    const result = await confirm('Are you sure to remove this page?')
    if (!result) return

    const newConfigs = cloneDeep(configs)
    newConfigs.splice(index, 1)
    setConfigs(newConfigs)
    setChanged(true)
  }

  const onAddControl = (index: number) => {
    const newConfigs = cloneDeep(configs)
    const inputs = (newConfigs[index] as any).inputs || []
    inputs.push({
      title: '',
      loanField: '',
    })
    ;(newConfigs[index] as any).inputs = inputs
    setConfigs(newConfigs)
    setChanged(true)
  }

  const onMoveControl = (index: number, subIndex: number, dir: 1 | -1) => {
    const newConfigs = cloneDeep(configs)
    const config = newConfigs[index]
    if (config.type != 'form') return

    if (subIndex + dir < 0 || subIndex + dir >= config.inputs.length) return

    const curItem = config.inputs[subIndex]
    config.inputs[subIndex] = config.inputs[subIndex + dir]
    config.inputs[subIndex + dir] = curItem
    setConfigs(newConfigs)
    setChanged(true)
  }

  const onRemoveControl = async (index: number, subIndex: number) => {
    const result = await confirm('Are you sure to remove this control?')
    if (!result) return

    const newConfigs = cloneDeep(configs)
    const config = newConfigs[index]
    if (config.type === 'form') config.inputs.splice(subIndex, 1)
    setConfigs(newConfigs)
    setChanged(true)
  }

  const getSelectOptions = (fieldOrigin: FieldOrigin, fieldKey: string): ISelectOption[] => {
    const input = inputFields[fieldOrigin][fieldKey] as InputSelect
    if (!input) return []
    let { options } = input

    if (Array.isArray(options))
      return options.map((v: string | ISelectRangeOption | ISelectValueOption) => {
        if (typeof v !== 'object')
          return {
            key: v,
            title: v,
            visible: true,
            isCustom: false,
          }
        let value = `${(v as ISelectValueOption).value || v.title}`
        if ((v as ISelectRangeOption).from !== undefined)
          value = `${(v as ISelectRangeOption).from}-${(v as ISelectRangeOption).to}`

        return {
          key: value,
          title: v.title,
          visible: v.visible ?? true,
          isCustom: false,
        }
      })
    return sortByKey(
      Object.keys(options).map((v) => ({ key: v, title: (options as any)[v], visible: true, isCustom: false })),
      'title',
    )
  }

  const onChangeControl = (index: number, subIndex: number, key: string, value: any) => {
    const newConfigs = cloneDeep(configs)
    let newConfig: IBorrowerLoanWorkflowSelect

    if (subIndex == -1) newConfig = newConfigs[index] as any
    else newConfig = (newConfigs[index] as IBorrowerLoanWorkflowForm).inputs[subIndex] as any
    ;(newConfig as any)[key] = value

    if (key === 'fieldOrigin' && value === FieldOrigin.Custom) {
      newConfig.fieldKey = `${Date.now()}`
    }

    if (key === 'fieldKey') {
      const input = inputFields[newConfig.fieldOrigin][value]
      if (input) {
        newConfig.title = input.title
        if (input.inputType?.toLowerCase() == 'select' && newConfig.fieldOrigin != FieldOrigin.Custom) {
          newConfig.options = getSelectOptions(newConfig.fieldOrigin, newConfig.fieldKey)
        }
      }
    }

    setConfigs(newConfigs)
    setChanged(true)
  }

  const onCopyBorrowerWorkflow = async () => {
    const result = await confirm('Are you sure to copy this Borrower loan pipeline configuration?')
    if (!result) return

    setLoading(true)
    getAdminConfig(`borrowerLoanWorkflow`)
      .then((configs) => {
        setConfigs(configs)
        setChanged(true)
      })
      .finally(() => setLoading(false))
  }

  const renderSelectOptionInputs = (index: number, subIndex: number) => {
    const isSelectPage = configs[index].type === 'select'
    let config: IBorrowerLoanWorkflowSelect = isSelectPage ? configs[index] : (configs[index] as any).inputs[subIndex]
    const { fieldOrigin, fieldKey, options = [] } = config
    const isCustomInput = fieldOrigin === FieldOrigin.Custom
    const isAccountControl = isAccountControlField(fieldOrigin, fieldKey)
    const isUtmCampaign = isUtmCampaignField(fieldOrigin, fieldKey)

    const onAddOption = () => {
      const newConfigs = cloneDeep(configs)
      const config: IBorrowerLoanWorkflowSelect = isSelectPage
        ? newConfigs[index]
        : (newConfigs[index] as any).inputs[subIndex]
      config.options = config.options || []
      config.options.push({
        key: `${Date.now()}`,
        title: '',
        visible: true,
        isCustom: true,
      })
      setConfigs(newConfigs)
    }

    let originalOptions = isCustomInput ? [] : getSelectOptions(fieldOrigin, fieldKey)

    const onChangeControlOption = (
      idx: number,
      newValue: { [P in keyof ISelectOption]?: NonNullable<ISelectOption[P]> } | null,
    ) => {
      const newConfigs = cloneDeep(configs)
      let newConfig: IBorrowerLoanWorkflowSelect

      if (subIndex == -1) newConfig = newConfigs[index] as any
      else newConfig = (newConfigs[index] as IBorrowerLoanWorkflowForm).inputs[subIndex] as any

      if (!newValue) newConfig.options.splice(idx, 1)
      else {
        newConfig.options[idx] = {
          ...newConfig.options[idx],
          ...newValue,
        }
      }

      console.log('newConfigs', newConfigs)

      setConfigs(newConfigs)
      setChanged(true)
    }

    const onChangeControlOptionOrder = (idx: number, dir: 1 | -1) => {
      const newConfigs = cloneDeep(configs)
      let newConfig: IBorrowerLoanWorkflowSelect

      if (subIndex == -1) newConfig = newConfigs[index] as any
      else newConfig = (newConfigs[index] as IBorrowerLoanWorkflowForm).inputs[subIndex] as any

      const newIdx = idx + dir
      if (newIdx < 0 || newIdx >= newConfig.options.length) return
      else {
        let temp: ISelectOption = newConfig.options[idx]
        newConfig.options[idx] = newConfig.options[newIdx]
        newConfig.options[newIdx] = temp
      }

      setConfigs(newConfigs)
      setChanged(true)
    }

    const renderAdditionalComponent = (option: ISelectOption, idx: number) => {
      const { isCustom, mapTo } = option

      const onSelectAccount = async () => {
        const result = await openUserSearchDlg({
          title: 'Select Account',
          inputTitle: 'User Email',
          value: option.email || '',
        })
        if (!result) return
        const name = toCamelCase(result.name)
        console.log('result', idx, result)
        onChangeControlOption(idx, { mapTo: `${result.id}`, name, email: result.email, title: result.name })
      }

      if (isAccountControl) {
        return (
          <span className="text-shade-blue text-sm cursor-pointer hover:underline" onClick={onSelectAccount}>
            {option.email || 'Select Account'}
          </span>
        )
      }

      if (isCustom && !isCustomInput && !isUtmCampaign)
        return (
          <select
            id="status-filter-group"
            className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
            value={mapTo}
            onChange={(e) => onChangeControlOption(idx, { mapTo: e.target.value })}
          >
            <option value="-"> </option>
            {originalOptions.map((opt) => (
              <option value={opt.key} key={opt.key}>
                {opt.title}
              </option>
            ))}
          </select>
        )
    }

    const onCopyOption = (value: string | undefined) => {
      if (!value) return
      if (isAccountControl) {
        const userId = value
        copyClipboard(`${document.location.origin}/new/${userId}`)
      } else if (isUtmCampaign) {
        const title = value
        const utmCampaign = title.toLowerCase().replace(/\s+/g, '-')
        copyClipboard(`${document.location.origin}/?utm_campaign=${utmCampaign}`)
      } else {
        copyClipboard(value)
      }
    }

    return (
      <div className="grid gap-2">
        <div className="flex justify-between">
          <p className="font-bold text-sm">Select Options</p>
          <span className="btn-icon" onClick={onAddOption}>
            <PlusIcon className="w-4 h-4" />
          </span>
        </div>
        {options.map((opt, idx) => {
          const { key, title, visible, isCustom } = opt
          const isVisible = !!visible || visible === undefined
          const Icon = isVisible ? EyeIcon : EyeSlashIcon
          let keyTitle = key
          if (fieldKey == 'productType' && keyTitle == 'DSCR (Long Term Rental)') keyTitle = 'DSCR (Rental Loan)'

          return (
            <Input2
              title={`${idx + 1}. ${isCustom ? '' : keyTitle}`}
              value={title}
              key={`Option-${idx}-${key}`}
              placeholder={title}
              className={isVisible ? 'opacity-100' : 'opacity-30'}
              onChange={(value: string) => onChangeControlOption(idx, { title: value })}
              additionalElements={() => (
                <div className="flex gap-2">
                  {renderAdditionalComponent(opt, idx)}
                  <Icon
                    className="w-4 h-4 cursor-pointer"
                    onClick={() => onChangeControlOption(idx, { visible: !isVisible })}
                  />
                  {isCustom && (
                    <>
                      <Tooltip message={`Edit Value - ${key}`}>
                        <PencilSquareIcon
                          className="w-4 h-4 cursor-pointer text-shade-blue"
                          onClick={async () => {
                            const newKey = await prompt(`Update the option value - ${title}`, { value: key })
                            if (!newKey) return
                            onChangeControlOption(idx, { key: newKey })
                          }}
                        />
                      </Tooltip>
                      <TrashIcon
                        className="w-4 h-4 cursor-pointer text-red-500"
                        onClick={() => onChangeControlOption(idx, null)}
                      />
                    </>
                  )}
                  <ArrowUpIcon
                    className="w-4 h-4 cursor-pointer text-shade-blue"
                    onClick={() => onChangeControlOptionOrder(idx, -1)}
                  />
                  <ArrowDownIcon
                    className="w-4 h-4 cursor-pointer text-shade-blue"
                    onClick={() => onChangeControlOptionOrder(idx, 1)}
                  />
                  {((isAccountControl && opt.name) ||
                    (isUtmCampaign && title) ||
                    (!isAccountControl && !isUtmCampaign)) && (
                    <DocumentDuplicateIcon
                      className="w-4 h-4 text-shade-blue cursor-pointer"
                      onClick={() => onCopyOption(isAccountControl ? opt.name : title)}
                    />
                  )}
                </div>
              )}
            />
          )
        })}
      </div>
    )
  }

  const renderVisibleLogicOR = (control: IBorrowerLoanWorkflowControl, index: number, subIndex: number) => {
    const onChangeVisibleLogic = (newValue: IVisibleLogic[][]) => {
      const newConfigs = cloneDeep(configs)
      let newConfig: IBorrowerLoanWorkflowSelect

      if (subIndex == -1) newConfig = newConfigs[index] as any
      else newConfig = (newConfigs[index] as IBorrowerLoanWorkflowForm).inputs[subIndex] as any

      newConfig.visibleLogic = newValue
      setConfigs(newConfigs)
      setChanged(true)
    }

    return (
      <InputVisibleLogic
        id={`visible-logic-${index}-${subIndex}`}
        value={control.visibleLogic || []}
        fieldInputs={inputFields as any}
        options={{ hasCustomOrigin: true, hasOtherOrigin: true, useMultiSelectForSelectInput: false }}
        onChange={onChangeVisibleLogic}
      />
    )
  }

  const renderCustomMapping = (control: IBorrowerLoanWorkflowCustomInput, index: number, subIndex: number) => {
    const inputs: Record<string, InputType> = {}
    const { linkedFieldOrigin, linkedFieldKey } = control

    inputs.linkedFieldOrigin = {
      inputType: 'select',
      title: 'Page Origin',
      options: fieldOriginOptionsExceptCustom,
      hasDefaultOption: true,
      value: linkedFieldOrigin,
      required: true,
      sort: false,
    }

    if (linkedFieldOrigin) {
      const options = allInputOptions[linkedFieldOrigin]
      inputs.linkedFieldKey = {
        inputType: 'select',
        title: 'Select Field',
        options: options || {},
        hasDefaultOption: true,
        value: linkedFieldKey,
        required: true,
        sort: true,
      }
    }

    return (
      <div className="mt-4">
        <p className="font-bold text-sm mb-2">Link Custom Field to</p>

        <div className="grid md:grid-cols-2 gap-x-2">
          {Object.keys(inputs).map((key) => {
            const elementKey = `${index}-${subIndex}-${linkedFieldOrigin}-${linkedFieldKey}-${key}`
            if (inputs[key].visible === false) return null

            return (
              <div className={`mb-4`} key={elementKey}>
                <RenderInput
                  input={inputs[key]}
                  Key={elementKey}
                  onChange={(_key: string, value: string) => onChangeControl(index, subIndex, key, value)}
                />
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const renderControl = (control: IBorrowerLoanWorkflowControl, index: number, subIndex: number) => {
    const { fieldOrigin, fieldKey, title, tooltip, required = true } = control

    const inputs: Record<string, InputType> = {}
    const isFormControl = subIndex != -1

    inputs.fieldOrigin = {
      inputType: 'select',
      title: 'Page Origin',
      options: isFormControl ? fieldOriginOptions : fieldOriginOptionsExceptCustom,
      hasDefaultOption: true,
      value: fieldOrigin,
      required: true,
      sort: false,
    }

    const isCustomField = fieldOrigin === FieldOrigin.Custom
    const options = subIndex == -1 ? selectInputOptions[fieldOrigin] : allInputOptions[fieldOrigin]
    inputs.fieldKey = {
      inputType: 'select',
      title: 'Select Field',
      options: options || {},
      hasDefaultOption: true,
      value: control.fieldKey,
      required: true,
      sort: true,
      visible: !isCustomField,
    }
    let fieldType: CustomFieldType | null = null
    if (isCustomField) {
      ;({ fieldType } = control as IBorrowerLoanWorkflowCustomInput)
      inputs.fieldType = {
        inputType: 'select',
        title: 'Field Type',
        options: CustomFieldTypeOptions,
        hasDefaultOption: true,
        value: fieldType,
        required: true,
      }
    }

    if (subIndex != -1) {
      const RequiredIcon = required ? BoltIcon : BoltSlashIcon

      inputs.title = {
        inputType: 'text',
        title: 'Title',
        value: title,
        additionalElements: (
          <>
            <Tooltip message={required ? 'Required' : 'Not Required'}>
              <span className="btn-icon" onClick={() => onChangeControl(index, subIndex, 'required', !required)}>
                <RequiredIcon className="w-4 h-4 text-gray-500" />
              </span>
            </Tooltip>
          </>
        ),
      }

      const isMonthlyField = isMonthlyInput({ fieldOrigin, fieldKey })
      if (isMonthlyField) {
        inputs.title.additionalElements = (
          <div className="flex flex-wrap items-center gap-3">
            <select
              name="plan"
              id="status-filter-group"
              className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
              value={(control as any).monthly}
              onChange={(e) => onChangeControl(index, subIndex, 'monthly', e.target.value)}
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
            {inputs.title.additionalElements || <></>}
          </div>
        )
      }

      inputs.tooltip = {
        inputType: 'textarea',
        title: 'Tooltip',
        value: tooltip,
        rows: 2,
      }
    }

    const input = fieldOrigin ? inputFields?.[fieldOrigin]?.[fieldKey] || {} : null

    const visibilityInputs: Record<string, InputType> = {}
    if (fieldOrigin) {
      visibilityInputs.visibility = {
        inputType: 'section',
        title: `Visibility Logic (OR)`,
      }
    }

    return (
      <div className="grid lg:grid-cols-2 gap-x-2 mb-4" key={`control-${index}-${subIndex}`}>
        {isFormControl && (
          <div className="col-span-full mb-4">
            <div className={`border-b-2 border-shade-blue w-full italic flex justify-between items-center`}>
              <p>- Control {subIndex + 1}</p>

              <div className="flex items-center gap-2">
                <p className="btn-icon" onClick={() => onMoveControl(index, subIndex, -1)}>
                  <ArrowUpIcon className="w-4 h-4" />
                </p>
                <p className="btn-icon" onClick={() => onMoveControl(index, subIndex, 1)}>
                  <ArrowDownIcon className="w-4 h-4" />
                </p>
                <p className="text-sm cursor-pointer" onClick={() => onRemoveControl(index, subIndex)}>
                  - Remove
                </p>
              </div>
            </div>
          </div>
        )}

        <div>
          <div className="grid md:grid-cols-2 gap-x-2">
            {Object.keys(inputs).map((key) => {
              const elementKey = `${index}-${subIndex}-${fieldOrigin}-${fieldKey}-${key}`
              if (inputs[key].visible === false) return null

              return (
                <div
                  className={`mb-4 ${!['fieldOrigin', 'fieldKey', 'fieldType'].includes(key) ? 'col-span-full' : ''}`}
                  key={elementKey}
                >
                  <RenderInput
                    input={inputs[key]}
                    Key={elementKey}
                    onChange={(_key: string, value: string) => onChangeControl(index, subIndex, key, value)}
                  />
                </div>
              )
            })}
          </div>
          {((input && input.inputType?.toLowerCase() === 'select') ||
            (fieldOrigin == FieldOrigin.Custom && fieldType === CustomFieldType.Select)) &&
            renderSelectOptionInputs(index, subIndex)}
        </div>

        <div>
          {renderVisibleLogicOR(control, index, subIndex)}
          {isCustomField && renderCustomMapping(control as IBorrowerLoanWorkflowCustomInput, index, subIndex)}
          <LinkToParty
            value={control.linkToParty}
            onChange={(v: any) => onChangeControl(index, subIndex, 'linkToParty', v)}
          />
        </div>
      </div>
    )
  }

  const renderForm = (inputs: IBorrowerLoanWorkflowControl[], index: number) => {
    return (
      <>
        {inputs.map((input, subIndex: number) => renderControl(input, index, subIndex))}
        <div className="col-span-full">
          <Button link onClick={() => onAddControl(index)} className="-mt-4">
            + Add Form Control
          </Button>
        </div>
      </>
    )
  }

  const renderStepContent = (item: IBorrowerLoanWorkflowStep, index: number) => {
    switch (item.type) {
      case 'form':
        return renderForm(item.inputs || [], index)
      case 'select':
        return renderControl(item, index, -1)
    }
  }

  const renderConfig = (item: IBorrowerLoanWorkflowStep, index: number) => {
    const inputs: Record<string, InputType> = {
      header: {
        inputType: 'section',
        title: `Page ${index + 1}`,
        span: 'full',
        additionalElements: (
          <div className="flex items-center gap-2">
            {item.type == 'form' && (
              <p className="btn-icon" onClick={() => onChangePageOrder(index)}>
                <BarsArrowDownIcon className="w-4 h-4" />
              </p>
            )}
            <p className="btn-icon" onClick={() => onMovePage(index, -1)}>
              <ArrowUpIcon className="w-4 h-4" />
            </p>
            <p className="btn-icon" onClick={() => onMovePage(index, 1)}>
              <ArrowDownIcon className="w-4 h-4" />
            </p>
            <p className="text-sm cursor-pointer" onClick={() => onRemovePage(index)}>
              - Remove
            </p>
          </div>
        ),
      },
      headerText: {
        inputType: 'text',
        title: 'Header',
        span: 'full',
        value: item.headerText || defaultHeaderText,
      },
      title: {
        inputType: 'text',
        title: 'Title',
        value: item.title,
      },
      type: {
        inputType: 'select',
        title: 'Form Type',
        options: {
          select: 'Select',
          form: 'Form',
        },
        hasDefaultOption: true,
        value: item.type,
      },
    }

    return (
      <div className={`pt-4 px-4 ${index % 2 == 1 ? 'bg-slate-50' : ''}`} key={`config-${index}`}>
        {Object.keys(inputs).map((key) => {
          return (
            <div className={`mb-4 ${['header', 'headerText'].includes(key) ? '' : 'max-w-[50%]'} pr-2`} key={key}>
              <RenderInput
                input={inputs[key]}
                Key={key}
                onChange={(key: string, value: string) => onChange(index, key, value)}
              />
            </div>
          )
        })}
        <div>{renderStepContent(item, index)}</div>
      </div>
    )
  }

  const onEtcChange = (key: string, value: any) => {
    const temp = cloneDeep(etcConfig)
    temp[pageType][key] = value
    setEtcConfig(temp)
  }

  const onEtcBlur = () => {
    setAdminConfig('loanWorkflow', etcConfig)
  }

  const renderEtcConfig = useMemo(() => {
    return (
      <>
        {Object.keys(etcConfigInputs).map((key) => {
          const input = cloneDeep(etcConfigInputs[key])
          input.value = etcConfig[pageType]?.[key]
          return (
            <div className={`col-span-${input.span || 1}`} key={key}>
              <RenderInput input={input} Key={key} onChange={onEtcChange} onBlur={onEtcBlur} />
            </div>
          )
        })}
      </>
    )
  }, [etcConfig, pageType])

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Customize {capitalizeFirstLetter(pageType)} Application</h1>
        {!isBorrower && (
          <Button link onClick={onCopyBorrowerWorkflow}>
            Copy from Borrower
          </Button>
        )}
      </div>
      <Prompt
        when={changed}
        message={`You've made some changes on Customize ${capitalizeFirstLetter(
          pageType,
        )} Application!\nAre you sure want to leave without Saving?`}
      />
      <LayoutLoading show={isLoading || !etcConfig[pageType]} />

      <div className="px-4 grid grid-cols-2">{renderEtcConfig}</div>

      {configs.map((item, index) => renderConfig(item, index))}
      {!!errorFields.length ? (
        <p className="text-sm text-red-700 bg-red-50 border border-red-600 rounded mb-4 p-4 inline-block mt-4">
          Below fields are mandatary. Please add them and save.
          <br />
          {errorFields.map((field, index) => (
            <React.Fragment key={`field-${index}`}>
              - {fieldOriginOptions[field.fieldOrigin]} / {allInputOptions[field.fieldOrigin][field.fieldKey]}
              <br />
            </React.Fragment>
          ))}
        </p>
      ) : null}
      <div className="flex flex-row justify-center gap-4">
        <Button link onClick={onAddStep}>
          + Add New Page
        </Button>
        <Button disabled={!changed} onClick={onSave}>
          Save
        </Button>
      </div>
      <SaveChanges show={changed} label="Save Changes" onSave={onSave} />
      {orderPageIndex != -1 && (
        <PageOrderModal
          inputs={(configs[orderPageIndex] as IBorrowerLoanWorkflowForm).inputs}
          onClose={(newInputs: IBorrowerLoanWorkflowControl[] | null) => {
            if (newInputs) {
              const newConfigs = cloneDeep(configs)
              ;(newConfigs[orderPageIndex] as IBorrowerLoanWorkflowForm).inputs = newInputs
              setConfigs(newConfigs)
              setChanged(true)
            }
            setOrderPageIndex(-1)
          }}
        />
      )}
    </div>
  )
}
